import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { getLoadDisplayId } from '../utils/loadUtils';

const CancelLoadModal = ({ isOpen, onClose, onConfirm, load, isLoading }) => {
  // Memoize the TONU warning check
  const shouldShowTONUWarning = React.useMemo(() => {
    return load?.status.toLowerCase().includes('assigned') || load?.status.toLowerCase().includes('assigned_driver');
  }, [load?.status]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={onClose}
            />

            {/* Modal */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-md z-[101]"
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>

              {/* Warning Icon */}
              <div className="mb-6 flex justify-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
              </div>

              {/* Content */}
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Cancel Load #{getLoadDisplayId(load)}?
                </h3>
                <div className="space-y-3">
                  {shouldShowTONUWarning ? (
                    <p className="text-gray-600">
                      Cancelling this load will incur a <span className="font-semibold text-gray-800">$250 TONU fee</span> (Truck Ordered Not Used).
                    </p>
                  ) : (
                    <p className="text-gray-600">
                      Are you sure you want to cancel this load?
                    </p>
                  )}
                  <p className="text-gray-600">
                    This action cannot be undone. The load will be marked as cancelled and all parties will be notified.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={isLoading}
                >
                  Keep Load
                </button>
                <button
                  onClick={onConfirm}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  disabled={isLoading}
                >
                  {isLoading ? 'Cancelling...' : shouldShowTONUWarning ? 'Cancel Load ($250 fee)' : 'Cancel Load'}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CancelLoadModal; 